import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Building2, MapPin, Phone, Power, CheckCircle,
  RefreshCw, Clock, ChevronRight, AlertTriangle, XCircle,
  Loader2, Calendar, RotateCcw, MessageSquare, Search, X, Trash2
} from 'lucide-react';
import { lifecycleAPI } from '../../../services/api';
import { LIFECYCLE_STATUS, OFFBOARDING_STEPS, STEP_STATUS } from '../constants';
import { useAuth } from '../../../context/AuthContext';
import { userHasRole } from '../../../utils/roleHelpers';
import OffboardingWorkflowPanel from '../components/OffboardingWorkflowPanel';

const OffboardingWorkflow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Phase 15.2 — `:id` from `/hotel-lifecycle/offboarding/:id` is the
  // authoritative way to deep-link / refresh into a specific hotel's
  // offboarding workflow. `location.state` is kept for back-compat.
  const { id: urlHotelId } = useParams();
  const { user } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [stepNotes, setStepNotes] = useState({});
  const [offboardingReason, setOffboardingReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = userHasRole(user, 'admin');
  const bulkDeleteEnabled = localStorage.getItem('bulk_delete_enabled') === 'true';

  // Phase 16 — `?focus_step=<step_id>` deep-link from notification bell
  // tells the offboarding panel which step card to auto-expand + scroll
  // to. Read once on mount; recomputes only if the URL changes.
  const focusStep = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('focus_step') || null;
  }, [location.search]);
  const [selectedIds, setSelectedIds] = useState([]);

  // Filter hotels based on search query
  const filteredHotels = useMemo(() => {
    if (!searchQuery.trim()) return hotels;
    
    const query = searchQuery.toLowerCase();
    return hotels.filter(hotel => {
      const hotelName = (hotel.propertyName || hotel.hotelName || '').toLowerCase();
      const ownerName = (hotel.ownerName || '').toLowerCase();
      const city = (hotel.city || '').toLowerCase();
      const phone = (hotel.mobile || hotel.phone || '').toLowerCase();
      
      return hotelName.includes(query) || 
             ownerName.includes(query) || 
             city.includes(query) || 
             phone.includes(query);
    });
  }, [hotels, searchQuery]);

  useEffect(() => {
    loadOnboardedHotels();
  }, []);

  // Phase 15.2 — URL `:id` is the authoritative deep-link. If present,
  // we auto-load that hotel's lifecycle and jump straight into the 7-step
  // workflow view. This makes refresh, browser-back/forward, copy-paste
  // sharing of the URL, and the LifecycleDashboard "Start Offboarding"
  // modal all work without depending on transient `location.state`.
  //
  // Fallback to `location.state.autoSelectHotelId` (Phase 15.1 contract)
  // is preserved so existing callers (HotelLifecycleDetail.handleStartOffboarding)
  // continue to work without any code change on their side.
  useEffect(() => {
    const autoId = urlHotelId || location.state?.autoSelectHotelId || location.state?.propertyId;
    if (autoId) {
      loadHotelDetails(autoId);
    }
    // Intentionally only depends on urlHotelId so changing the URL via
    // navigate(`/hotel-lifecycle/offboarding/${otherId}`) re-loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlHotelId]);

  const loadOnboardedHotels = async () => {
    try {
      setLoading(true);
      const response = await lifecycleAPI.getAll();
      // Phase 15 — Filter only properties that are 100% onboarded AND in a
      // live/onboarded status. The Start Offboarding dropdown must NEVER
      // surface partially-onboarded properties.
      const onboardedHotels = (response.data || []).filter(hotel => {
        const propStatus = hotel.propertyStatus;
        const lcStatus = hotel.lifecycleStatus;
        const fullyOnboarded = (hotel.progress || hotel.lifecycleProgress || 0) >= 100;
        const eligibleStatus = (
          propStatus === 'Onboarded' || propStatus === 'Live' || propStatus === 'Active' ||
          lcStatus === 'onboarded' || lcStatus === 'live'
        );
        return fullyOnboarded && eligibleStatus;
      });
      setHotels(onboardedHotels);
    } catch (error) {
      console.error('Error loading hotels:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHotelDetails = async (hotelId) => {
    try {
      const response = await lifecycleAPI.getById(hotelId);
      setSelectedHotel(response.data);
    } catch (error) {
      console.error('Error loading hotel details:', error);
    }
  };

  const handleSelectHotel = async (hotel) => {
    // Phase 15.2 — clicking a hotel in the list pushes a deep-link URL so
    // refresh + browser back/forward all work. The URL-param effect picks it up.
    navigate(`/hotel-lifecycle/offboarding/${hotel.id}`);
  };

  const handleBackToList = () => {
    setSelectedHotel(null);
    // Phase 15.2 — if user deep-linked via `/hotel-lifecycle/offboarding/:id`,
    // strip the id from the URL so the list view stays in sync with browser
    // history (otherwise the URL-param effect would immediately re-load the
    // same hotel and the back-button would feel broken).
    if (urlHotelId) {
      navigate('/hotel-lifecycle/offboarding', { replace: true });
    }
    loadOnboardedHotels();
  };

  // Kept (unused now) for callers that still expect this name. Same behaviour
  // as handleSelectHotel — list clicks always push a real URL.
  const handleSelectFromList = handleSelectHotel;

  const handleOffboardingBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected records?`)) return;
    try {
      await lifecycleAPI.bulkDelete(selectedIds);
      setSelectedIds([]);
      loadOnboardedHotels();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Failed to delete records');
    }
  };

  const toggleOffboardingSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleOffboardingSelectAll = () => {
    if (selectedIds.length === filteredHotels.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredHotels.map(h => h.id));
    }
  };

  // Start offboarding for a hotel — Phase 15
  const handleStartOffboarding = async (reason = '') => {
    if (!selectedHotel) return;
    const trimmedReason = (reason || '').trim();
    if (!trimmedReason) {
      alert('Reason for offboarding is required');
      return;
    }
    if (!window.confirm('Are you sure you want to start offboarding this hotel? This will begin the offboarding workflow.')) return;

    try {
      setActionLoading('startOffboarding');
      // Phase 15 — Single backend call handles 100% gate, step1 auto-
      // complete, reason persistence, and notifications.
      await lifecycleAPI.startOffboarding(selectedHotel.id, { reason: trimmedReason });
      await loadHotelDetails(selectedHotel.id);
      loadOnboardedHotels();
    } catch (error) {
      console.error('Error starting offboarding:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to start offboarding';
      alert(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  // Cancel offboarding - restore to Onboarding status
  const handleCancelOffboarding = async () => {
    if (!selectedHotel) return;
    if (!window.confirm('Are you sure you want to cancel offboarding? The property will return to Onboarding status.')) return;
    
    try {
      setActionLoading('cancelOffboarding');
      // Call API to cancel offboarding
      await lifecycleAPI.cancelOffboarding(selectedHotel.id);
      alert('Offboarding cancelled. Property returned to Onboarding status.');
      handleBackToList();
    } catch (error) {
      console.error('Error cancelling offboarding:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to cancel offboarding';
      alert(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  // Start a workflow step
  const handleStartStep = async (stepId) => {
    if (!selectedHotel) return;
    try {
      setActionLoading(`start_${stepId}`);
      await lifecycleAPI.startStep(selectedHotel.id, 'offboarding', stepId);
      await loadHotelDetails(selectedHotel.id);
    } catch (error) {
      console.error('Error starting step:', error);
      alert('Failed to start step');
    } finally {
      setActionLoading(null);
    }
  };

  // Complete a workflow step
  const handleCompleteStep = async (stepId) => {
    if (!selectedHotel) return;
    try {
      setActionLoading(`complete_${stepId}`);
      const notes = stepNotes[stepId] || '';
      
      // For request_initiated step, include reason
      if (stepId === 'request_initiated' && offboardingReason) {
        await lifecycleAPI.updateOffboardingStep(selectedHotel.id, stepId, { reason: offboardingReason });
      }
      
      await lifecycleAPI.completeStep(selectedHotel.id, 'offboarding', stepId, notes);
      await loadHotelDetails(selectedHotel.id);
      setStepNotes(prev => ({ ...prev, [stepId]: '' }));
    } catch (error) {
      console.error('Error completing step:', error);
      alert('Failed to complete step');
    } finally {
      setActionLoading(null);
    }
  };

  // Check if offboarding is in progress
  const isOffboardingInProgress = selectedHotel && (
    selectedHotel.propertyStatus === 'Offboarding' ||
    selectedHotel.lifecycleStatus === 'offboarding_in_progress'
  );

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Render hotel list view
  const renderHotelList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/lifecycle')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-to-lifecycle"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">Offboarding Workflow</h1>
            <p className="text-gray-500 mt-1">Select an Onboarded property to start offboarding</p>
          </div>
          <button
            onClick={loadOnboardedHotels}
            className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-600 rounded-xl hover:bg-orange-100 transition-colors font-medium"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          {isAdmin && bulkDeleteEnabled && selectedIds.length > 0 && (
            <button
              onClick={handleOffboardingBulkDelete}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium shadow-sm"
              data-testid="offboarding-bulk-delete-btn"
            >
              <Trash2 size={18} />
              Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="relative">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by hotel name, owner name, city, or phone number..."
            className="w-full pl-12 pr-10 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
            data-testid="offboarding-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-sm text-gray-500 mt-2">
            Found {filteredHotels.length} of {hotels.length} properties
          </p>
        )}
      </div>

      {/* Onboarded Hotels List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-100 rounded w-1/2 mt-2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredHotels.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {searchQuery ? <Search size={40} className="text-gray-400" /> : <CheckCircle size={40} className="text-green-400" />}
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {searchQuery ? 'No Properties Found' : 'No Onboarded Properties'}
          </h3>
          <p className="text-gray-500">
            {searchQuery 
              ? 'Try adjusting your search query'
              : 'Only properties with "Onboarded" status can be offboarded.'}
          </p>
          {searchQuery ? (
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Clear Search
            </button>
          ) : (
            <button
              onClick={() => navigate('/lifecycle')}
              className="mt-4 px-6 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-colors font-medium"
            >
              Back to Lifecycle
            </button>
          )}
        </div>
      ) : (
        <div>
          {isAdmin && bulkDeleteEnabled && filteredHotels.length > 0 && (
            <div className="flex items-center gap-3 mb-4 px-2">
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.length === filteredHotels.length && filteredHotels.length > 0}
                  onChange={toggleOffboardingSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  data-testid="offboarding-select-all"
                />
                Select All ({selectedIds.length}/{filteredHotels.length})
              </label>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredHotels.map(hotel => (
            <div
              key={hotel.id}
              className="relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-orange-200 transition-all cursor-pointer group"
              data-testid={`offboarding-hotel-card-${hotel.id}`}
            >
              {isAdmin && bulkDeleteEnabled && (
                <div className="absolute top-3 left-3 z-10" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(hotel.id)}
                    onChange={() => toggleOffboardingSelect(hotel.id)}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    data-testid={`offboarding-select-${hotel.id}`}
                  />
                </div>
              )}
              <div onClick={() => handleSelectHotel(hotel)}>
              {/* Orange accent for offboarding eligible */}
              <div className="h-1.5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-t-2xl" />
              
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                      {hotel.propertyName?.charAt(0) || hotel.hotelName?.charAt(0) || 'H'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 group-hover:text-orange-600 transition-colors">
                        {hotel.propertyName || hotel.hotelName || 'Unnamed Property'}
                      </h3>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                        <MapPin size={12} />
                        <span>{hotel.city}{hotel.state ? `, ${hotel.state}` : ''}</span>
                      </div>
                      {hotel.mobile && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                          <Phone size={12} />
                          <span>{hotel.mobile}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-300 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
                </div>
                
                <div className="mt-4 flex items-center justify-between">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                    Onboarded
                  </span>
                  <span className="text-xs text-gray-400">Click to start offboarding</span>
                </div>
              </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render offboarding workflow for selected hotel
  const renderOffboardingWorkflow = () => {
    const offboardingSteps = selectedHotel?.offboardingSteps || {};
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-start gap-4">
            <button
              onClick={handleBackToList}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-to-list"
            >
              <ArrowLeft size={20} />
            </button>
            
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white font-bold text-2xl shadow-md">
              {selectedHotel.propertyName?.charAt(0) || 'H'}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-800">{selectedHotel.propertyName || selectedHotel.hotelName}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isOffboardingInProgress 
                    ? 'bg-orange-100 text-orange-700 border border-orange-200' 
                    : 'bg-green-100 text-green-700 border border-green-200'
                }`}>
                  {isOffboardingInProgress ? 'Offboarding' : 'Onboarded'}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <MapPin size={14} />
                  <span>{selectedHotel.city}{selectedHotel.state ? `, ${selectedHotel.state}` : ''}</span>
                </div>
                {selectedHotel.mobile && (
                  <div className="flex items-center gap-2">
                    <Phone size={14} />
                    <span>{selectedHotel.mobile}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {isOffboardingInProgress && (
                <button
                  onClick={handleCancelOffboarding}
                  disabled={actionLoading === 'cancelOffboarding'}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                  data-testid="cancel-offboarding-btn"
                >
                  {actionLoading === 'cancelOffboarding' ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <RotateCcw size={18} />
                  )}
                  Cancel Offboarding
                </button>
              )}
              <button
                onClick={() => loadHotelDetails(selectedHotel.id)}
                className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Start Offboarding Section (if not started yet) */}
        {!isOffboardingInProgress && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Power size={24} className="text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800">Start Offboarding Process</h3>
                <p className="text-gray-600 mt-1">Begin the offboarding workflow for this property. You can provide a reason for offboarding.</p>
                
                {/* Reason Input */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Offboarding Reason</label>
                  <textarea
                    value={offboardingReason}
                    onChange={(e) => setOffboardingReason(e.target.value)}
                    placeholder="Enter the reason for offboarding this property..."
                    rows={3}
                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none"
                    data-testid="offboarding-reason-input"
                  />
                </div>
                
                <button
                  onClick={() => handleStartOffboarding(offboardingReason)}
                  disabled={actionLoading === 'startOffboarding'}
                  className="mt-4 flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-sm"
                  data-testid="start-offboarding-workflow-btn"
                >
                  {actionLoading === 'startOffboarding' ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Power size={18} />
                  )}
                  Start Offboarding Workflow
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Offboarding Steps — Phase 15.6 fix.
            Previously this view rendered the legacy "Start Step / Complete
            Step" buttons which:
              (a) ignored role-based dropdowns required by Phase 15.5 spec
              (b) showed stale state after Step 1 completion → user had to
                  navigate back to LifecycleDashboard for the new dropdown
                  UI to appear.
            We now render <OffboardingWorkflowPanel> — the same role-gated
            7-step dropdown component used by HotelLifecycleDetail.jsx.
            `loadHotelDetails(selectedHotel.id)` is passed as `onAfterAction`
            so completing any step instantly refetches the lifecycle and
            re-renders the next step's dropdown with NO refresh/reopen. */}
        {isOffboardingInProgress && (
          <OffboardingWorkflowPanel
            hotel={selectedHotel}
            role={(user?.role || '').toLowerCase()}
            isAdmin={isAdmin}
            focusStep={focusStep}
            onAfterAction={() => loadHotelDetails(selectedHotel.id)}
          />
        )}
      </div>
    );
  };

  return selectedHotel ? renderOffboardingWorkflow() : renderHotelList();
};

export default OffboardingWorkflow;
